import { clone } from "lodash";

import { omittedValues } from "./qrEditorHelper";
import { getTokenData } from "@/services/CookiesService";
import { isSubscriptionActive } from "@/services/UserService";
import { amplitudeLogoutUser } from "@/services/AmplitudeService/AmplitudeService";
import { QREditorAPItoDataConversor } from "@/services/ApiConversorService/QREditor/QrEditorApiToData";

import { paths } from "./paths";
import { isClient } from "./browser";
import { stripLocaleFromPath } from "./locale";
import { ApiErrorMessageBuilder } from "./apiErrorHandler";
import { isInlineHandledQrError } from "./qrSaveErrors";

import { isUserLoggedIn, isValidToken } from "@/hooks/auth/useLogin";
import { setPendingActionCookie } from "@/actions/setPendingActionCookie";
import { getRoleFromToken } from "@/contexts/UserContext/utils";

import { ActionTypes } from "@/internals/constants/actionTypes";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";

import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { QrCodeResponse } from "@/interface/api/getAllQrCodes/types";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { UserRole, UserStatus } from "@/types/user";
import { UpdateIndexInterface } from "./types";

const dispatchNavigation = (path: string, routerFn: AppRouterInstance, isReplace?: boolean) => {
  if (isClient()) {
    const navigate = isReplace ? routerFn.replace : routerFn.push;

    navigate(path);
  }
};

export const QR_PAGE_INDEX = {
  SELECT_QR_TYPE: 0,
  ADD_CONTENT: 1,
  DESIGN_QR: 2,
  REGISTER: 3,
  PRICING: 4,
  PAYMENT: 5,
};

export const getPageIndexFromUrl = (url: string) => {
  if (!url) return;

  const stripedLocaleFromPath = stripLocaleFromPath(url);
  const pageIndex = stripedLocaleFromPath.split("/").filter((substr) => substr).length;
  return pageIndex - 1;
};

export const getPageNameFromUrl = (url: string) => {
  if (!url) return;

  const baseUrlLastIndex = url.lastIndexOf("qr-editor");
  const stringWithoutBaseUrl = url.substring(baseUrlLastIndex + 10);

  return stringWithoutBaseUrl.at(-1) === "/"
    ? stringWithoutBaseUrl.slice(0, -1)
    : stringWithoutBaseUrl;
};

export const navigateFromQrToEditor = (
  qrEditorContext: QREditorContextProps<any>,
  data: QrCodeResponse,
  routerFn: AppRouterInstance
) => {
  const qrData = QREditorAPItoDataConversor.getQRData(data);
  const qrType = qrData.type;

  qrEditorContext.setQrData({
    ...qrData,
    currentPageIndex: QR_PAGE_INDEX.ADD_CONTENT,
  });
  qrEditorContext.setIsQRfinished(true);
  dispatchNavigation(`/qr-editor/${qrType}`, routerFn);
};

export const navigateBack = () => {
  if (isClient()) {
    window.history.go(-1);
  }
};

export const navigateToQrPage = (
  type: string,
  pageIndex: number,
  _isSubscriptionActive: boolean,
  routerFn: AppRouterInstance,
  isUnregisteredNavigation?: boolean,
  setSource?: React.Dispatch<string>
) => {
  let urlToNavigate = "";

  switch (pageIndex) {
    case QR_PAGE_INDEX.SELECT_QR_TYPE:
      urlToNavigate = paths.qrEditor();
      dispatchNavigation(urlToNavigate, routerFn, isUnregisteredNavigation);

      break;
    case QR_PAGE_INDEX.ADD_CONTENT:
      urlToNavigate = paths.qrEditor(type);
      dispatchNavigation(urlToNavigate, routerFn, isUnregisteredNavigation);

      break;
    case QR_PAGE_INDEX.DESIGN_QR:
      urlToNavigate = paths.qrEditor(type, "design");
      dispatchNavigation(urlToNavigate, routerFn, isUnregisteredNavigation);
      break;
    default:
      if (isUserLoggedIn()) {
        isValidToken().then((profileCall) => {
          profileCall().then((profile: { data: GetProfileResponse }) => {
            const { data } = profile;
            const hasTrial = data.status?.toLowerCase() === UserStatus.TRIAL.toLowerCase();

            const isActiveOrTrial = hasTrial || data?.subscription?.status === "active";
            const role = getRoleFromToken(getTokenData()?.token);
            const shouldGoToPricing = !isActiveOrTrial && role === UserRole.ADMIN;

            urlToNavigate = shouldGoToPricing ? paths.pricing() : paths.myQrCodes();
            dispatchNavigation(urlToNavigate, routerFn, isUnregisteredNavigation);
          });
        });
      } else {
        setPendingActionCookie({
          type: ActionTypes.EDITOR_LOGIN,
          redirectTo: paths.myQrCodes(),
        }).then(() => {
          setSource && setSource("editor");
          amplitudeLogoutUser();
          routerFn.push(paths.signup());
        });
      }
      break;
  }
};

const updateData = (newIndex: number, qrEditorContext: QREditorContextProps<undefined>) => {
  const newQRData = clone(qrEditorContext?.qrData);

  newQRData.previousPageIndex = newQRData.currentPageIndex;
  newQRData.currentPageIndex = newIndex;

  return newQRData;
};

export const updateIndex = (props: UpdateIndexInterface) => {
  const {
    newIndex,
    qrEditorContext,
    userContext,
    unregisteredNavigation = false,
    request,
    toast,
    router,
    setSource,
    tfunction,
  } = props;
  const { qrData } = qrEditorContext;
  if (!qrData.type) return;

  if (qrEditorContext?.validationError) {
    qrEditorContext?.qrData.type === "apps" &&
      toast({
        message: tfunction("add_app_link"),
        type: "error",
        marginBottom: 80,
      });
    qrEditorContext?.qrData.type === "social" &&
      toast({
        message: tfunction("add_social_link"),
        type: "error",
        marginBottom: 80,
      });
    return;
  }

  if (request) {
    request()
      .then((data) => {
        if (data !== undefined) {
          qrEditorContext?.setQrData(
            {
              ...updateData(newIndex, qrEditorContext),
              qrCodeId: data?.id || qrEditorContext.qrData.qrCodeId,
              qrCodeReference: data?.reference || qrEditorContext.qrData.qrCodeReference,
              qrStyle: qrEditorContext.qrData.qrStyle,
            },
            [...omittedValues(qrEditorContext?.qrData.data)]
          );

          navigateToQrPage(
            qrEditorContext?.qrData.type,
            newIndex,
            isSubscriptionActive(userContext),
            router,
            unregisteredNavigation,
            setSource
          );
        }
      })
      .catch((error) => {
        // Errors already shown inline by the form (duplicated reference, invalid URL) get no toast
        if (isInlineHandledQrError(error)) return;

        const errorMessage = ApiErrorMessageBuilder.buildToastMessage(error, {
          showDetails: true,
          includeCode: true,
          includeTimestamp: true,
          fallbackMessage: API_ERROR_FALLBACKS.QR_EDITOR.SAVE_PROGRESS,
        });
        toast({ message: errorMessage, type: "error", marginBottom: 80 });
      });
  } else {
    qrEditorContext?.setQrData(updateData(newIndex, qrEditorContext), [
      ...omittedValues(qrEditorContext?.qrData.data),
    ]);
    navigateToQrPage(
      qrEditorContext?.qrData.type,
      newIndex,
      isSubscriptionActive(userContext),
      router,
      unregisteredNavigation,
      setSource
    );
  }
};

export const handleBackButton = (qrEditorContext, routerFn): void => {
  if (
    isUserLoggedIn() &&
    qrEditorContext?.qrData.currentPageIndex === 1 &&
    qrEditorContext.isQRfinished
  ) {
    dispatchNavigation(paths.myQrCodes(), routerFn);
  }

  if (qrEditorContext?.qrData.currentPageIndex > 0) {
    qrEditorContext?.setQrData(
      updateData(qrEditorContext?.qrData.currentPageIndex - 1, qrEditorContext),
      [...omittedValues(qrEditorContext?.qrData.data)]
    );
    navigateBack();
  } else {
    !isUserLoggedIn()
      ? dispatchNavigation(paths.home(), routerFn)
      : dispatchNavigation(paths.myQrCodes(), routerFn);
  }
};

// The step in context moves forward before the route does. A Next click in between would skip
// the content form (no validation, no save) and land on the design step with empty data.
const isRouteBehindStep = (currentPageIndex: number) => {
  if (!isClient()) return false;

  const pageIndexFromUrl = getPageIndexFromUrl(window.location.pathname);
  return pageIndexFromUrl !== undefined && pageIndexFromUrl < currentPageIndex;
};

export const handleNextButton = (
  globalContext,
  qrEditorContext,
  userContext,
  requestQR,
  toast,
  isGridSelect,
  router,
  setSource?,
  tfunction?
) => {
  if (isRouteBehindStep(qrEditorContext?.qrData.currentPageIndex)) return;

  if (qrEditorContext?.qrData.currentPageIndex < QR_PAGE_INDEX.PAYMENT) {
    // Para evitar duplicidad de eventos en Amplitude
    // Se valida si el QR se ha seleccionado en qr-editor o al usar next button
    if (isGridSelect) qrEditorContext?.setGridSelectEvent(true);
    if (
      globalContext.context.formMethods &&
      qrEditorContext?.qrData.currentPageIndex !== QR_PAGE_INDEX.SELECT_QR_TYPE
    ) {
      globalContext.context.formMethods.handleSubmit(() => {
        updateIndex({
          newIndex: qrEditorContext?.qrData.currentPageIndex + 1,
          qrEditorContext,
          userContext,
          request: requestQR,
          toast,
          router,
          setSource,
          tfunction,
        });
      })();
    } else {
      updateIndex({
        newIndex: qrEditorContext?.qrData.currentPageIndex + 1,
        qrEditorContext,
        userContext,
        toast,
        router,
        tfunction,
      });
    }
  }
};

export const handleQRFooterDirectNavigation = (
  currentPageIndex,
  contextPageIndex,
  qrEditorContext,
  routerFn
) => {
  if (contextPageIndex === 0 && currentPageIndex > 0) {
    if (currentPageIndex === QR_PAGE_INDEX.ADD_CONTENT) {
      const pageName = getPageNameFromUrl(location.pathname);

      qrEditorContext.setQrData({
        ...qrEditorContext.qrData,
        currentPageIndex,
        type: pageName,
        data: {
          ...qrEditorContext.qrData.data,
          typeName: QREditorAPItoDataConversor.QRTypeTransform(pageName),
        },
      });
    } else if (
      currentPageIndex >= QR_PAGE_INDEX.DESIGN_QR ||
      currentPageIndex >= QR_PAGE_INDEX.REGISTER
    )
      dispatchNavigation(paths.qrEditor(), routerFn, true);
  }
};

export enum eventValidateTypes {
  QREDITOR = "qr-editor",
  CTABUTTONS = "cta_buttons",
  BROWSERURL = "browser_url",
}

export const validateEventSource = (eventSource) => {
  if (eventSource) {
    return eventSource !== eventValidateTypes.QREDITOR ? eventSource : undefined;
  } else {
    return eventValidateTypes.BROWSERURL;
  }
};
export const validatePricngEventSource = (eventSource) => {
  if (eventSource) {
    return eventSource !== eventValidateTypes.CTABUTTONS ? eventSource : undefined;
  }
};
